import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabDef<T extends string> {
  id: T;
  label: string;
}

/** Liste d'onglets accessible (flèches, Début, Fin). Le panneau associé se rend avec `TabPanel` et le même `prefix`. */
export function Tabs<T extends string>({
  label,
  tabs,
  active,
  onChange,
  prefix = 'tab',
}: {
  label: string;
  tabs: TabDef<T>[];
  active: T;
  onChange: (id: T) => void;
  prefix?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKey(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    const t = tabs[next];
    if (t) {
      onChange(t.id);
      refs.current[t.id]?.focus();
    }
  }

  return (
    <div role="tablist" aria-label={label} className="tabs">
      {tabs.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => {
            refs.current[t.id] = el;
          }}
          type="button"
          role="tab"
          id={`${prefix}-${t.id}`}
          aria-selected={active === t.id}
          aria-controls={`${prefix}-panel-${t.id}`}
          tabIndex={active === t.id ? 0 : -1}
          className="tab"
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({
  prefix = 'tab',
  id,
  children,
}: {
  prefix?: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <div role="tabpanel" id={`${prefix}-panel-${id}`} aria-labelledby={`${prefix}-${id}`}>
      {children}
    </div>
  );
}
